<script lang="ts" setup>
import { Mailing } from '@/@types'
import { Item } from '@/@types/cart'
import { Order } from '@/@types/order'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Link, useForm } from '@inertiajs/vue3'
import { Edit, Mail } from 'lucide-vue-next'
import UpdateGroup from '../admin/orders/UpdateGroup.vue'
import RemoveTicket from './RemoveTicket.vue'

withDefaults(
    defineProps<{
        mailing: Mailing
        orderItem: Item
        hasActions?: boolean
        isAdmin?: boolean
        status: string
        order: Order
    }>(),
    {
        hasActions: true,
        isAdmin: true,
    },
)

const emit = defineEmits<{
    (e: 'delete', mailingId: number, itemId?: number): void
}>()

function deleteChild(mailingId: number, itemId?: number) {
    emit('delete', mailingId, itemId)
}

const form = useForm({})
</script>

<template>
    <div class="mt-4 grid gap-8 gap-y-6 rounded-lg bg-gray-50 px-6 py-4 md:grid-cols-[1fr_1fr] lg:grid-cols-[1fr_2fr_1fr_60px]">
        <div class="flex flex-col gap-2 overflow-hidden">
            <span class="font-inter text-sm">{{ $t('Participant') }}</span>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger class="line-clamp-1 cursor-pointer text-left text-base font-semibold">
                        {{ mailing.personal_info.full_name }}
                    </TooltipTrigger>

                    <TooltipContent class="shadow-4xl" align="start" side="top">
                        <p>{{ mailing.personal_info.full_name }}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
        <div class="flex flex-col gap-2">
            <span class="font-inter text-sm">{{ $t('Email') }}</span>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger class="line-clamp-1 cursor-pointer text-left text-base font-semibold break-all">
                        {{ mailing.email }}
                    </TooltipTrigger>

                    <TooltipContent class="shadow-4xl" align="start" side="top">
                        <p>{{ mailing.email }}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>

        <UpdateGroup v-if="isAdmin" :order="order" :mailing="mailing" />
        <div v-else class="flex flex-col gap-2">
            <div v-if="mailing.status !== 'canceled'">
                <div class="flex cursor-pointer items-center gap-4">
                    {{ $t('Group') }}
                </div>
            </div>
            <div v-else>
                {{ $t('Group') }}
            </div>
            <p class="text-base font-semibold">
                {{ $t(`groups.${mailing.group}`) }}
            </p>
        </div>

        <div class="flex flex-col gap-2 lg:items-center" v-if="hasActions && (status === 'paid' || status === 'released')">
            <span class="font-inter text-sm">{{ $t('Actions') }}</span>
            <p class="flex items-center gap-3 text-base font-semibold">
                <RemoveTicket :item="orderItem" :mailing="mailing" :isAdmin="isAdmin" @delete="deleteChild" />

                <Link v-if="isAdmin" :href="`/admin/mailings/${mailing.id}/edit`" class="hover:text-primary w-6 cursor-pointer text-gray-400">
                    <Edit />
                </Link>
                <AlertDialog v-if="isAdmin" :href="`/admin/mailings/${mailing.id}/edit`">
                    <AlertDialogTrigger class="hover:text-primary w-6 cursor-pointer text-gray-400">
                        <Mail />
                    </AlertDialogTrigger>
                    <AlertDialogContent class="font-inter gap-2">
                        <AlertDialogHeader class="text-2xl font-semibold">Reenviar o e-mail</AlertDialogHeader>
                        <AlertDialogDescription class="text-foreground">
                            Um novo e-mail será enviado para o endereço cadastrado com o link de preenchimento do ingresso.
                        </AlertDialogDescription>
                        <AlertDialogFooter class="mt-4 flex justify-between gap-1">
                            <AlertDialogCancel class="flex-1" as-child>
                                <Button variant="ghost">Voltar</Button>
                            </AlertDialogCancel>
                            <form class="flex-1" @submit.prevent="form.post(`/admin/mailings/${mailing.id}/resend`, { preserveScroll: true })">
                                <AlertDialogAction class="w-full flex-1" type="submit"> Reenviar </AlertDialogAction>
                            </form>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </p>
        </div>
    </div>
</template>
